import { Component, signal, inject, computed, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../../services/settings/settings.service';
import { IdentityService } from '../../../services/identity/identity.service';
import { MessengerService } from '../../../services/messenger/messenger.service';
import { ToasterService } from '../../../services/toaster/toaster.service';
import { EditEmail } from './edit-email/edit-email';
import { AddPhone } from './add-phone/add-phone';
import { ChangePassword } from './change-password/change-password';
import { ChatService } from '../../../services/chat/chat.service';

@Component({
  selector: 'app-settings',
  imports: [FormsModule, EditEmail, AddPhone, ChangePassword],
  templateUrl: './settings.html',
  styleUrl: './settings.scss',
})
export class Settings implements OnInit {
  private settingsService = inject(SettingsService);
  private identityService = inject(IdentityService);
  private messengerService = inject(MessengerService);
  private chatService = inject(ChatService);
  private toaster = inject(ToasterService);

  currentUser = computed(() => {
    const claims = this.identityService.getClaims();
    return {
      name: claims?.unique_name || claims?.name || 'Rift_Walker',
      email: claims?.email || 'rift_walker@domain.com'
    };
  });

  currentUserAvatar = computed(() => this.messengerService.currentUser().avatar);

  // Active Tab State (bound to SettingsService)
  activeTab = this.settingsService.activeTab;

  // Reveal email state
  revealEmail = signal(false);

  // Inline editing states
  isEditingEmail = signal(false);
  isEditingPhone = signal(false);
  isEditingPassword = signal(false);

  emailDisplay = computed(() => {
    const email = this.currentUser().email;
    if (this.revealEmail()) {
      return email;
    }
    const parts = email.split('@');
    if (parts.length === 2) {
      return '********@' + parts[1];
    }
    return '********';
  });

  // Appearance settings (bound to SettingsService)
  fontColor = this.settingsService.fontColor;
  fontStyle = this.settingsService.fontStyle;
  userNameColor = this.settingsService.userNameColor;

  // Temporary appearance settings state
  tempFontColor = signal<string>(this.settingsService.fontColor());
  tempFontStyle = signal<string>(this.settingsService.fontStyle());
  tempUserNameColor = signal<string>(this.settingsService.userNameColor());

  // Audio settings (bound to SettingsService)
  masterVolume = this.settingsService.masterVolume;
  musicVolume = this.settingsService.musicVolume;
  sfxVolume = this.settingsService.sfxVolume;

  // Data & Privacy settings (bound to SettingsService)
  improveRivalForge = this.settingsService.improveRivalForge;
  personalizeSponsoredContent = this.settingsService.personalizeSponsoredContent;
  useThirdPartyData = this.settingsService.useThirdPartyData;
  personalizeExperience = this.settingsService.personalizeExperience;

  // Audio devices
  audioInputDevices = signal<MediaDeviceInfo[]>([]);
  audioOutputDevices = signal<MediaDeviceInfo[]>([]);
  selectedInputDevice = this.settingsService.selectedInputDevice;
  selectedOutputDevice = this.settingsService.selectedOutputDevice;
  micPermissionError = signal<boolean>(false);

  // Graphics settings
  enableShadows = signal(true);
  enableAnimations = signal(true);

  ngOnInit() {
    this.loadAudioDevices();
  }

  async loadAudioDevices() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.log("enumerateDevices() not supported.");
      return;
    }

    try {
      // Request permission to get device labels, then stop the stream
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      this.micPermissionError.set(false);

      const devices = await navigator.mediaDevices.enumerateDevices();
      this.audioInputDevices.set(devices.filter(device => device.kind === 'audioinput'));
      this.audioOutputDevices.set(devices.filter(device => device.kind === 'audiooutput'));
    } catch (err) {
      console.error('Error accessing media devices.', err);
      this.micPermissionError.set(true);
    }
  }

  setActiveTab(tabName: string) {
    this.activeTab.set(tabName);
  }

  toggleRevealEmail() {
    this.revealEmail.update(r => !r);
  }

  showComingSoon(feature: string) {
    this.toaster.info(`${feature} settings are coming soon!`);
  }

  applyAppearanceSettings() {
    const userColor = this.tempUserNameColor();
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (!hexRegex.test(userColor)) {
      this.toaster.warning('Please enter a valid hex color for Username Color (e.g., #9F50FF).');
      return;
    }

    const chatColor = this.tempFontColor();
    if (!hexRegex.test(chatColor)) {
      this.toaster.warning('Please enter a valid hex color for Chat Font Color (e.g., #FFFFFF).');
      return;
    }

    // Call the server API first to save username color
    this.settingsService.saveUserNameColorOnServer(userColor).subscribe({
      next: () => {
        // Once successfully saved on server, update all signals
        this.settingsService.userNameColor.set(userColor);
        this.settingsService.fontColor.set(chatColor);
        this.settingsService.fontStyle.set(this.tempFontStyle());
        this.messengerService.updateUser();
        this.chatService.updateUser();
        this.toaster.success('Appearance settings applied successfully!');
      },
      error: (err) => {
        console.error('Failed to update username color on server:', err);
        this.toaster.danger('Failed to save username color on the server.');
      }
    });
  }
}
